import React, { useCallback, useMemo } from 'react';
import { Button, Center, useDisclosure } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { useMostRecentTariff } from '../../bills/tariffs/tariffApi';
import { useGetCombinedSiteDeviceSchedules } from '../../scheduler/schedulerApi';
import { useGetSiteSolarSpongeConfigQuery } from '../../site/control-profile/siteSolarSpongeConfigApi';
import { selectSite } from '../../site/siteSlice';
import { SmartSaveStatus } from './smart-save-helpers';
import { selectSmartSaveDevices, selectSmartSaveStatus } from './smartSaveSlice';
import { UpdateTariffAlertDialog } from './UpdateTariffAlertDialog';
import { useSaveSmartSaveConfig } from './use-save-smartsave-config';
import { useSmartSaveStatus } from './use-smartsave-status';

export const SaveOptimisationsButton = () => {
  const navigate = useNavigate();
  const smartSaveDevices = useSelector(selectSmartSaveDevices);

  const { schedules: combinedSiteDeviceSchedules, isLoading: isLoadingCombinedSiteDeviceSchedules } =
    useGetCombinedSiteDeviceSchedules(smartSaveDevices.map((d) => d.site_device_id));

  const hasActiveSchedules = useMemo(
    () =>
      Object.values(combinedSiteDeviceSchedules)
        .flat()
        .some((schedule) => schedule.active),
    [combinedSiteDeviceSchedules]
  );

  const smartSaveStatus = useSelector(selectSmartSaveStatus);
  const { smartSaveStatus: smartSaveStatusAPI } = useSmartSaveStatus();
  const hasTurnedOnSmartSave = useMemo(
    // IF smartSaveStatusAPI is OFF and smartSaveStatus is ON or TRIAL, then it means the user has turned on SmartSave but not saved yet
    () =>
      smartSaveStatusAPI === SmartSaveStatus.OFF &&
      (smartSaveStatus === SmartSaveStatus.ON || smartSaveStatus === SmartSaveStatus.TRIAL),
    [smartSaveStatus, smartSaveStatusAPI]
  );
  const hasTurnedOffSmartSave = useMemo(
    // IF smartSaveStatusAPI is ON or TRIAL and smartSaveStatus is OFF, then it means the user has turned off SmartSave but not saved yet
    () =>
      (smartSaveStatusAPI === SmartSaveStatus.ON || smartSaveStatusAPI === SmartSaveStatus.TRIAL) &&
      smartSaveStatus === SmartSaveStatus.OFF,
    [smartSaveStatus, smartSaveStatusAPI]
  );
  const { site_id: siteId } = useSelector(selectSite);
  const { needToUpdateTariff, isLoading: isTariffsDataLoading } = useMostRecentTariff();
  const { data: siteSmartSaveConfig } = useGetSiteSolarSpongeConfigQuery(siteId);
  const { saveSmartSaveConfig, isUpdating } = useSaveSmartSaveConfig();
  const {
    isOpen: isUpdateTariffDialogOpen,
    onClose: onCloseUpdateTariffDialog,
    onOpen: onOpenUpdateTariffDialog,
  } = useDisclosure();
  const {
    isOpen: isTurnOffSmartSaveDialogOpen,
    onClose: onCloseTurnOffSmartSaveDialog,
    onOpen: onOpenTurnOffSmartSaveDialog,
  } = useDisclosure();
  const {
    isOpen: isTurnOnSmartSaveDialogOpen,
    onClose: onCloseTurnOnSmartSaveDialog,
    onOpen: onOpenTurnOnSmartSaveDialog,
  } = useDisclosure();

  const isDisabled = useMemo(() => {
    const smartSaveAPIActiveBitMap = siteSmartSaveConfig?.solar_sponge_devices?.map((d) => d.active) ?? [];
    const smartSaveUIActiveBitMap = smartSaveDevices.map((d) => d.active);

    // Ensures that if new devices exist, the save button is enabled
    if (smartSaveAPIActiveBitMap.length !== smartSaveUIActiveBitMap.length) return false;

    // Ensures that if the status has changed, the save button is enabled
    if (smartSaveStatus !== smartSaveStatusAPI) return false;

    return JSON.stringify(smartSaveAPIActiveBitMap) === JSON.stringify(smartSaveUIActiveBitMap);
  }, [siteSmartSaveConfig?.solar_sponge_devices, smartSaveDevices, smartSaveStatus, smartSaveStatusAPI]);

  const handleSaveOptimisations = useCallback(async () => {
    // If smart save was previously off and now it is on, and there are actives manual schedules, inform user that they will be overridden
    if (hasTurnedOnSmartSave && hasActiveSchedules) return onOpenTurnOnSmartSaveDialog();

    // If smart save was previously on and now it is off, inform user that all optimisations will be turned off
    if (hasTurnedOffSmartSave) return onOpenTurnOffSmartSaveDialog();

    await saveSmartSaveConfig();
  }, [
    hasActiveSchedules,
    hasTurnedOffSmartSave,
    hasTurnedOnSmartSave,
    onOpenTurnOffSmartSaveDialog,
    onOpenTurnOnSmartSaveDialog,
    saveSmartSaveConfig,
  ]);

  return (
    <>
      <Center mt="auto" mb={6}>
        <Button
          data-testid="save-optimisations-button"
          mt={6}
          alignSelf={['center', 'center', 'flex-start']}
          mb={2}
          rounded={20}
          colorScheme="dusk100"
          w={'70vw'}
          maxW={500}
          isDisabled={isDisabled || isLoadingCombinedSiteDeviceSchedules}
          isLoading={isUpdating || isTariffsDataLoading}
          // only show the update tariff dialog if the user has turned on smart save and there are tariffs to update
          onClick={needToUpdateTariff && hasTurnedOnSmartSave ? onOpenUpdateTariffDialog : handleSaveOptimisations}
        >
          Save
        </Button>
      </Center>

      <UpdateTariffAlertDialog
        isOpen={isUpdateTariffDialogOpen}
        onClose={onCloseUpdateTariffDialog}
        onCancel={() => {
          onCloseUpdateTariffDialog();
          handleSaveOptimisations();
        }}
        onConfirm={() => {
          onCloseUpdateTariffDialog();
          navigate(`/site/${siteId}/bills/tariffs/new/basic-details`);
        }}
      />

      <AlertDialogModal
        header={'Are you sure?'}
        subHeader={
          'Cortex will no longer optimise any of your devices. If you want to change settings for a specific device, go to the Live page.'
        }
        dialogContentTestId="turn-off-smart-save-dialog"
        isOpen={isTurnOffSmartSaveDialogOpen}
        onClose={onCloseTurnOffSmartSaveDialog}
        confirmButtonTextColor={'customRed.500'}
        onCancel={() => navigate(`/site/${siteId}/live`)}
        onConfirm={() => {
          onCloseTurnOffSmartSaveDialog();
          saveSmartSaveConfig();
        }}
        confirmButtonName="Proceed and turn off optimiser"
        cancelButtonName="Go to Live Page"
      />

      <AlertDialogModal
        header={'Are you sure?'}
        subHeader={'Optimiser will override any existing manual schedules.'}
        dialogContentTestId="turn-on-smart-save-dialog"
        isOpen={isTurnOnSmartSaveDialogOpen}
        onClose={onCloseTurnOnSmartSaveDialog}
        confirmButtonTextColor={'customRed.500'}
        onConfirm={async () => {
          onCloseTurnOnSmartSaveDialog();
          await saveSmartSaveConfig();
        }}
        onCancel={onCloseTurnOnSmartSaveDialog}
        confirmButtonName="Proceed"
        cancelButtonName="Cancel"
      />
    </>
  );
};
